import { Card, Fade, Skeleton, useTheme } from "@mui/material";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  TooltipItem,
} from "chart.js";
import { ReactNode, useMemo } from "react";
import { Bar, Line } from "react-chartjs-2";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import {
  DashboardColor,
  pulseSx,
  surfaceCardSx,
} from "@/components/screens/ServiceDashboard/components/styles";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip,
  Legend
);

export type TrendSeries = {
  label: string;
  values: (number | null)[];
  color: DashboardColor;
  dashed?: boolean;
};

type Props = {
  title: string;
  labels: string[];
  series: TrendSeries[];
  type?: "line" | "bar";
  emptyLabel: string;
  valueFormatter?: (value: number) => string;
  yMax?: number;
  height?: string;
  showLegend?: boolean;
  isLoading?: boolean;
  isRefreshing?: boolean;
  /** Conteúdo exibido ao lado do gráfico (ex.: totais). */
  aside?: ReactNode;
};

const TrendChartCard = ({
  title,
  labels,
  series,
  type = "line",
  emptyLabel,
  valueFormatter = (value) =>
    new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 }).format(value),
  yMax,
  height = "10rem",
  showLegend = false,
  isLoading = false,
  isRefreshing = false,
  aside,
}: Props) => {
  const theme = useTheme();
  const paletteColor = (color: DashboardColor): string =>
    (theme.palette as unknown as Record<string, { main?: string }>)[color]?.main ??
    theme.palette.primary.main;

  const hasData = series.some((item) => item.values.some((value) => value !== null && value !== 0));

  const data = useMemo(
    () => ({
      labels,
      datasets: series.map((item) => ({
        label: item.label,
        data: item.values,
        borderColor: paletteColor(item.color),
        backgroundColor:
          type === "bar" ? `${paletteColor(item.color)}e6` : paletteColor(item.color),
        borderDash: item.dashed ? [5, 4] : undefined,
        borderWidth: item.dashed ? 1.5 : 2,
        pointRadius: item.dashed || type === "bar" ? 0 : 2.5,
        pointHoverRadius: 4,
        tension: 0.3,
        spanGaps: true,
        borderRadius: type === "bar" ? 2 : undefined,
        categoryPercentage: 0.75,
        barPercentage: 0.85,
        maxBarThickness: 48,
      })),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [labels, series, type, theme]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        legend: {
          display: series.length > 1 || showLegend,
          position: "top" as const,
          align: "start" as const,
          labels: {
            boxWidth: 14,
            boxHeight: 2,
            padding: 10,
            font: { size: 10 },
            usePointStyle: false,
          },
        },
        tooltip: {
          callbacks: {
            label: (context: TooltipItem<"line" | "bar">) =>
              context.parsed.y === null || context.parsed.y === undefined
                ? `${context.dataset.label}: --`
                : `${context.dataset.label}: ${valueFormatter(context.parsed.y)}`,
          },
        },
      },
      scales: {
        x: {
          // Linhas verticais leves ligam cada ponto ao seu dia no eixo.
          grid: { display: true, color: theme.palette.grey[100], drawTicks: true },
          // Rótulos verticais (90°) ficam centralizados sob o ponto; em 45° o
          // texto termina no ponto e parece pertencer ao dia ao lado.
          ticks: { font: { size: 9 }, autoSkip: false, minRotation: 90, maxRotation: 90 },
        },
        y: {
          beginAtZero: true,
          max: yMax,
          ticks: {
            font: { size: 9 },
            maxTicksLimit: 5,
            callback: (value: string | number) => valueFormatter(Number(value)),
          },
          grid: { color: theme.palette.grey[200] },
          border: { display: false },
        },
      },
    }),
    [series.length, valueFormatter, yMax, theme, showLegend]
  );

  return (
    <Card sx={surfaceCardSx()}>
      <MDBox px={1.75} pt={1.25} pb={0.25}>
        <MDTypography variant="button" fontWeight="bold" display="block">
          {title}
        </MDTypography>
      </MDBox>
      {isLoading ? (
        <MDBox px={1.75} pb={1.5}>
          <Skeleton variant="rounded" height={height} />
        </MDBox>
      ) : !hasData ? (
        <MDBox px={1.75} pb={1.5} height={height}>
          <MDTypography variant="button" color="text">
            {emptyLabel}
          </MDTypography>
        </MDBox>
      ) : (
        <Fade in timeout={520}>
          <MDBox
            px={1.25}
            pb={1}
            height={height}
            display="flex"
            gap={1.5}
            alignItems="stretch"
            sx={pulseSx(isRefreshing)}
          >
            <MDBox flex={1} minWidth={0} position="relative">
            {type === "bar" ? (
              <Bar
                data={data as ChartData<"bar", (number | null)[], string>}
                options={options as ChartOptions<"bar">}
              />
            ) : (
              <Line
                data={data as ChartData<"line", (number | null)[], string>}
                options={options as ChartOptions<"line">}
              />
            )}
            </MDBox>
            {aside}
          </MDBox>
        </Fade>
      )}
    </Card>
  );
};

export default TrendChartCard;
